import { ClientMessage, ServerMessage, encode, decode } from '@deepwake/shared';
import { runGallery } from './gallery.js';

const galleryKind = new URLSearchParams(location.search).get('gallery');
if (galleryKind !== null) {
  runGallery(galleryKind || 'ships');
  throw new Error('gallery-mode'); // halt normal boot (caught by nobody on purpose)
}
import { ClientSim } from './sim.js';
import { Renderer } from './render.js';
import { Hud } from './hud.js';
import { StationScreen } from './station.js';
import { CodexUi } from './codexui.js';
import { Cinematics, jumpFlash } from './cinematics.js';
import { Maps } from './maps.js';
import { Sound } from './audio.js';
import { MobileShell } from './mobile.js';

const canvas = document.getElementById('view') as HTMLCanvasElement;
const hudRoot = document.getElementById('hud')!;
const loginEl = document.getElementById('login')!;

const sim = new ClientSim();
const renderer = new Renderer(canvas);

let ws: WebSocket | null = null;
let docked: number | null = null;

function send(m: ClientMessage): void {
  if (ws && ws.readyState === WebSocket.OPEN) ws.send(encode(m));
}

const hud = new Hud(hudRoot, {
  onModule: (slot, active) => send(active ? { t: 'activate', slot } : { t: 'deactivate', slot }),
  onTune: (value) => send({ t: 'tune', value }),
  onCommand: (cmd, targetId, arg) => {
    switch (cmd) {
      case 'lock':
        codex.step('lock');
        if (targetId !== null) send({ t: 'lock', targetId });
        return;
      case 'unlock':
        return send({ t: 'unlock' });
      case 'loot':
        if (targetId !== null) send({ t: 'loot', wreckId: targetId });
        return;
      case 'stop':
        return send({ t: 'stop' });
      case 'undock':
        return send({ t: 'undock' });
      case 'approach':
        codex.step('fly');
        if (targetId !== null) send({ t: 'approach', targetId });
        return;
      case 'orbit':
        codex.step('fly');
        if (targetId !== null) send({ t: 'orbit', targetId, range: arg ?? 5000 });
        return;
      case 'keepRange':
        if (targetId !== null) send({ t: 'keepRange', targetId, range: arg ?? 10000 });
        return;
      case 'warpTo':
        codex.step('warp');
        sound.play('warp', 1500);
        if (targetId !== null) send({ t: 'warpTo', targetId });
        return;
      case 'dock':
        if (targetId !== null) send({ t: 'dock', stationId: targetId });
        return;
      case 'jump':
        if (targetId !== null) send({ t: 'jump', gateId: targetId });
        return;
    }
  },
  onChat: (text) => send({ t: 'say', text }),
});

const codex = new CodexUi(document.body);
const cinema = new Cinematics();
const sound = new Sound();
const mobile = new MobileShell();
void mobile;
const maps = new Maps({
  onSelect: (id) => hud.select(id, sim),
  onWarp: (id) => {
    codex.step('warp');
    send({ t: 'warpTo', targetId: id });
  },
});
const station = new StationScreen(document.getElementById('station')!, {
  send: (m) => send(m as ClientMessage),
});

function connect(handle: string): void {
  let token = localStorage.getItem('deepwake.token');
  if (!token) {
    token = crypto.randomUUID();
    localStorage.setItem('deepwake.token', token);
  }
  localStorage.setItem('deepwake.handle', handle);

  const proto = location.protocol === 'https:' ? 'wss' : 'ws';
  ws = new WebSocket(`${proto}://${location.host}/ws`);
  ws.onopen = () => send({ t: 'hello', handle, token: token! });
  ws.onclose = () => {
    hud.toast('Disconnected — reconnecting…');
    setTimeout(() => connect(handle), 2500);
  };
  ws.onmessage = (e) => {
    const m = decode<ServerMessage>(String(e.data));
    if (!m) return;
    switch (m.t) {
      case 'welcome':
        maps.setSystem(m.system);
        loginEl.classList.add('hidden');
        if (!localStorage.getItem('deepwake.introSeen')) {
          localStorage.setItem('deepwake.introSeen', '1');
          cinema.playIntro(() => {});
        }
        docked = m.docked;
        hud.setSystem(m.system);
        renderer.setSystemSkybox(m.system.id);
        if (docked !== null) {
          hud.toast(`Docked in ${m.system.name}.`);
          send({ t: 'stationInfo' });
        }
        break;
      case 'fullstate':
        sim.applyFullstate(m.balls, m.tick, m.yourBallId);
        hud.setSystem(m.system);
        renderer.setSystemSkybox(m.system.id);
        break;
      case 'delta':
        sim.applyDelta(m.events, m.tick);
        break;
      case 'docked':
        sound.play('dock', 500);
        codex.step('dock');
        docked = m.stationId;
        sim.myBallId = null;
        hud.select(null, sim);
        hud.updateStatus(null);
        send({ t: 'stationInfo' });
        break;
      case 'undocked':
        docked = null;
        station.hide();
        codex.step('undock');
        hud.toast('Undocked.');
        break;
      case 'stationInfo':
        station.show(m);
        break;
      case 'status':
        hud.updateStatus(m);
        break;
      case 'notice':
        if (m.text.startsWith('Refined:')) codex.step('refine');
        if (m.text.startsWith('Foundry delivered:')) codex.step('build');
        if (m.text.includes('crates delivered') || m.text.startsWith('Mission')) codex.step('mission');
        hud.toast(m.text);
        break;
      case 'minedOre':
        sound.play('mine', 2500);
        codex.step('mine');
        hud.toast(`+${m.qty} ${m.ore} (rock: ${m.remaining} left)`);
        break;
      case 'combat':
        if (m.kind === 'hit') hud.toast(`${m.from === sim.myBallId ? 'Hit for' : 'Took'} ${m.dmg} (${Math.round(m.quality * 100)}%)`);
        break;
      case 'destroyed':
        hud.toast('Target destroyed');
        break;
      case 'story':
        cinema.playStory(m.chapter, m.title, m.body, m.objective, m.done);
        break;
      case 'youDied':
        cinema.playShellcast(m.lost);
        hud.toast(`Ship lost: ${m.lost}. Your core shellcasts home...`);
        docked = m.respawnStation;
        sim.reset();
        sim.myBallId = null;
        hud.updateStatus(null);
        setTimeout(() => send({ t: 'stationInfo' }), 800);
        break;
      case 'jumped':
        sound.play('warp', 500);
        maps.setSystem(m.system);
        jumpFlash();
        codex.step('gate');
        hud.setSystem(m.system);
        renderer.setSystemSkybox(m.system.id);
        hud.select(null, sim);
        hud.toast(`Jumped to ${m.system.name}`);
        break;
      case 'fx':
        if (m.kind === 'tracer' || m.kind === 'miss' || m.kind === 'beam') {
          if (m.from !== undefined && m.to !== undefined) renderer.vfx.spawnLine(m.kind, m.from, m.to);
          if (m.kind !== 'beam') sound.play('shot', 200);
        } else if (m.kind === 'shieldhit' && m.to !== undefined) {
          renderer.vfx.spawnShieldHit(m.to);
          sound.play('shieldhit', 250);
        } else if (m.kind === 'explosion') {
          renderer.vfx.spawnExplosion(null, m.from, m.size ?? 15);
          sound.play('explosion', 300);
        }
        break;
      case 'chat':
        hud.chat(m.from, m.text);
        break;
      case 'error':
        hud.toast(m.message);
        break;
    }
  };
}

// Login flow
const handleInput = document.getElementById('handle') as HTMLInputElement;
handleInput.value = localStorage.getItem('deepwake.handle') ?? '';
document.getElementById('loginbtn')!.addEventListener('click', () => {
  const h = handleInput.value.trim();
  if (h.length >= 3) connect(h);
});
handleInput.addEventListener('keydown', (e) => {
  if (e.key === 'Enter') (document.getElementById('loginbtn') as HTMLButtonElement).click();
});

// Click-to-select in space
canvas.addEventListener('click', (e) => {
  if (renderer.dragDistance > 8) return; // that was a camera drag, not a tap
  const id = renderer.pick(sim, e.clientX, e.clientY, performance.now());
  if (id !== null) hud.select(id, sim);
});

// QA hook (headless UI tests read camera state through this)
(window as unknown as { __dw: unknown }).__dw = { renderer, sim, hud };

// Keyboard shortcuts
addEventListener('keydown', (e) => {
  if ((e.target as HTMLElement)?.tagName === 'INPUT') return;
  if (e.key === 'm' || e.key === 'M') maps.toggle('system');
  if (e.key === 'n' || e.key === 'N') maps.toggle('reach');
  // F1..F8 fire module slots
  const fkey = /^F([1-8])$/.exec(e.key);
  if (fkey && hud.lastStatus) {
    e.preventDefault();
    const i = Number(fkey[1]) - 1;
    const mod = hud.lastStatus.modules[i];
    if (mod) send(mod.active ? { t: 'deactivate', slot: i } : { t: 'activate', slot: i });
  }
});

// Render + HUD loop
let lastHud = 0;
function frame(now: number): void {
  renderer.render(sim, now);
  if (now - lastHud > 250) {
    lastHud = now;
    hud.update(sim, docked);
    maps.draw(sim);
  }
  requestAnimationFrame(frame);
}
requestAnimationFrame(frame);
